import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, User, Phone, Mail, Eye } from 'lucide-react';
import { api } from '../../services/api';
import { PageHeader, DataTable, Column, SortConfig, SearchInput } from '../../components/ui';
import { Badge, Card, buttonClass, type BadgeTone } from '../../design-system';
import { formatPhone, formatCpfCnpj, formatDate } from '../../utils/format';
import { PAGE_SIZE, tipoPessoaLabels } from '../../constants';

const tipoPessoaTones: Record<string, BadgeTone> = {
  PF: 'info',
  PJ: 'success',
};

interface ClienteList {
  id: number;
  nome: string;
  tipoPessoa: string;
  cpfCnpj: string | null;
  email: string | null;
  telefone: string | null;
  celular: string | null;
  cidade: string | null;
  uf: string | null;
  dataCadastro: string;
}

interface PagedResult {
  items: ClienteList[];
  totalItems: number;
  page: number;
  pageSize: number;
}

export function ClientesList() {
  const navigate = useNavigate();
  const [data, setData] = useState<PagedResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [busca, setBusca] = useState('');
  const [tipoPessoa, setTipoPessoa] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'nome', direction: 'asc' });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: PAGE_SIZE.toString(),
        tipo: 'CLIENTE',
      });
      if (busca) params.append('busca', busca);
      if (tipoPessoa) params.append('tipoPessoa', tipoPessoa);
      if (sortConfig.direction) {
        params.append('orderBy', sortConfig.key);
        params.append('orderDirection', sortConfig.direction);
      }

      const response = await api.get<PagedResult>(`/pessoas?${params}`);
      setData(response.data);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page, busca, tipoPessoa, sortConfig]);

  const handleSort = (config: SortConfig) => {
    setSortConfig(config);
    setPage(1);
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchData();
    return () => controller.abort();
  }, [fetchData]);

  const columns: Column<ClienteList>[] = [
    {
      key: 'nome',
      header: 'Nome',
      sortable: true,
      render: (item) => (
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-50 ring-1 ring-primary-100">
            <User className="h-5 w-5 text-primary-600" />
          </div>
          <div>
            <div className="font-medium text-slate-900">{item.nome}</div>
            {item.cpfCnpj && (
              <div className="text-sm text-slate-500">{formatCpfCnpj(item.cpfCnpj)}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'tipoPessoa',
      header: 'Tipo',
      sortable: true,
      render: (item) => (
        <Badge tone={tipoPessoaTones[item.tipoPessoa] || 'neutral'}>
          {tipoPessoaLabels[item.tipoPessoa] || item.tipoPessoa}
        </Badge>
      ),
    },
    {
      key: 'celular',
      header: 'Contato',
      sortable: true,
      render: (item) => (
        <div className="space-y-1">
          {item.celular && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Phone className="h-4 w-4 text-slate-400" />
              <span>{formatPhone(item.celular)}</span>
            </div>
          )}
          {item.email && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Mail className="h-4 w-4 text-slate-400" />
              <span className="max-w-[200px] truncate">{item.email}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'cidade',
      header: 'Cidade',
      sortable: true,
      render: (item) => (
        item.cidade ? `${item.cidade}${item.uf ? ` - ${item.uf}` : ''}` : '-'
      ),
    },
    {
      key: 'dataCadastro',
      header: 'Cadastro',
      sortable: true,
      render: (item) => formatDate(item.dataCadastro),
    },
    {
      key: 'actions',
      header: '',
      width: '50px',
      render: (item) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/clientes/${item.id}`);
          }}
          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-primary-600"
          title="Ver detalhes"
        >
          <Eye className="h-4 w-4" />
        </button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="Clientes" subtitle="Gerencie seus clientes">
        <button
          onClick={() => navigate('/clientes/novo')}
          className={buttonClass('primary')}
        >
          <Plus className="h-4 w-4" />
          Novo Cliente
        </button>
      </PageHeader>

      {/* Filters */}
      <Card className="mb-6 p-4">
        <div className="flex flex-wrap gap-4">
          <SearchInput
            value={busca}
            onChange={setBusca}
            placeholder="Buscar por nome, CPF/CNPJ, email ou telefone..."
          />
          <select
            value={tipoPessoa}
            onChange={(e) => {
              setTipoPessoa(e.target.value);
              setPage(1);
            }}
            className="h-10 rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          >
            <option value="">Todos</option>
            <option value="PF">Pessoa Física</option>
            <option value="PJ">Pessoa Jurídica</option>
          </select>
        </div>
      </Card>

      {/* Table */}
      <DataTable
        columns={columns}
        data={data?.items || []}
        keyField="id"
        isLoading={isLoading}
        emptyMessage="Nenhum cliente encontrado"
        page={page}
        pageSize={PAGE_SIZE}
        totalItems={data?.totalItems}
        onPageChange={setPage}
        sortConfig={sortConfig}
        onSort={handleSort}
        onRowClick={(item) => navigate(`/clientes/${item.id}`)}
      />
    </div>
  );
}
